import { BranchFilter } from "@/data/models/BranchFilter";
import ClientPanel from "@/data/models/ClientPanel";
import { GenericDeviceFilter, matchesGenericDeviceFilter } from "@/data/models/DeviceFilter";
import { DeviceOverview } from "@/data/models/DeviceOverview";
import { isClientPanel } from "@/data/models/DeviceSettingsManager";
import router from "@/router/router";
import { CockpitEntryInfo, CockpitEntryInfoType } from "./CockpitProjectsInfoCreator";
import { setNumber } from "./SecureLocalStorage";
import { setStoredBranchFilterId, removeStoredBranchFilterId } from "@/utils/BranchFilterStorage";

const storageKeyAllDevicesFilter = "storageKeyAllDevicesFilter";
const storageKeyBranchFilter = "AllDevicesBranchFilter";

interface CalculateAllDevicesContext {
  vue: Vue;
  branchFilter: BranchFilter;
}

function openOtherDevicesList(
  deviceFilter: GenericDeviceFilter,
  context: CalculateAllDevicesContext
) {
  const { branchFilter } = context;

  setNumber(storageKeyAllDevicesFilter, deviceFilter);
  
  if (branchFilter.branch?.id) {
    setStoredBranchFilterId(storageKeyBranchFilter, branchFilter.branch.id);
  }
  else {
    removeStoredBranchFilterId(storageKeyBranchFilter);
  }

  router.push({
    path: "allDevices"
  });
}

function getNumOtherDevices(
  filter: GenericDeviceFilter,
  otherDevices: Array<DeviceOverview | ClientPanel>,
  context: CalculateAllDevicesContext
) {
  const { branchFilter } = context;
  const forceStrictCheck = branchFilter.branch?.id ? true : false;

  return otherDevices.filter((otherDevice) => {
    return (
      branchFilter.doesMatchGenericDevice(otherDevice, forceStrictCheck) &&
      matchesGenericDeviceFilter(
        filter,
        otherDevice
    ))
  }).length;
}

export function calculateAllDevicesItems(
  otherDevices: (DeviceOverview | ClientPanel)[],
  context: CalculateAllDevicesContext,
  clientPanelsInUsage: number,
) {
  if (otherDevices.length === 0) {
    return [];
  }

  let id = 0;
  const result: CockpitEntryInfo[] = [];

  const numAllDevices = getNumOtherDevices(
    GenericDeviceFilter.ALL_DEVICES,
    otherDevices,
    context
  );

  const numInUsage = getNumOtherDevices(
    GenericDeviceFilter.IN_USAGE,
    otherDevices.filter((device) => !isClientPanel(device)),
    context
  ) + clientPanelsInUsage;

  const numAvailable = getNumOtherDevices(
    GenericDeviceFilter.AVAILABLE,
    otherDevices,
    context
  );

  const numNotAvailable = getNumOtherDevices(
    GenericDeviceFilter.NOT_AVAILABLE,
    otherDevices,
    context
  );

  const { vue } = context;

  if (numAllDevices) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.overall").toString(),
        count: numAllDevices,
      },
      onClickCallback: () => {
        openOtherDevicesList(GenericDeviceFilter.ALL_DEVICES, context);
      },
    });
  }

  if (numInUsage) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.inUsage").toString(),
        count: numInUsage,
      },
      onClickCallback: () => {
        openOtherDevicesList(
          GenericDeviceFilter.IN_USAGE,
          context
        );
      },
    });
  }

  if (numAvailable) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.available").toString(),
        count: numAvailable,
      },
      onClickCallback: () => {
        openOtherDevicesList(
          GenericDeviceFilter.AVAILABLE,
          context
        );
      },
    });
  }

  if (numNotAvailable) {
    result.push({
      id: id++,
      shouldBeDisplayed: true,
      infoType: CockpitEntryInfoType.Counter,
      data: {
        name: vue.$t("deviceFilter.notAvailable").toString(),
        count: numNotAvailable,
      },
      onClickCallback: () => {
        openOtherDevicesList(GenericDeviceFilter.NOT_AVAILABLE, context);
      },
    });
  }

  return result;
}